'use client';

// <LiveCountdown> — a self-ticking countdown to an instant (BL-596 / ADR 024).
// Computes remaining time via @auctogon/time's `timeRemaining` against an
// optional server-anchored clock, so a skewed device clock can't change the
// displayed time-remaining. At zero it renders `endedLabel` — a hand-off state,
// never an outcome: the caller confirms closed/extended from its own source of
// truth, never from this timer.
//
// NOTE (BL-1682): this component has no production consumer today — the clerk
// console runs one shared timer for its whole list (`CatalogCountdown`) and the
// bidder's surfaces pass their own label. The default below was updated with the
// rest of the zero-state vocabulary so it cannot ship the retired wording if a
// caller does appear, but it is NOT the clerk console's source. Kept rather than
// deleted because this is public package surface (rule #30's named exception).

import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { timeRemaining, type TimeInput, type TimeRemaining } from '@auctogon/time';

export interface LiveCountdownProps {
  /** The instant the countdown runs to. */
  end: TimeInput;
  /** Optional start — before it, the countdown counts down to the start. */
  start?: TimeInput | null;
  /**
   * Server-anchored "now" (epoch ms). Defaults to the local clock. Pass a
   * server-corrected reader so the displayed time-remaining is honest even on a
   * skewed device clock. Read fresh each tick (picks up a late offset).
   */
  nowMs?: () => number;
  /** Custom renderer for the remaining time; defaults to a compact "1h 2m 3s". */
  children?: (remaining: TimeRemaining) => ReactNode;
  /** Rendered when the countdown reaches zero (status `ended`). Default "Awaiting result". */
  endedLabel?: ReactNode;
  /**
   * Fires exactly once when the countdown crosses into `ended` (now ≥ end).
   * Re-arms when `end`/`start` change (an anti-snipe extension can complete
   * the fresh window later). Use for a "go live" / "closed" transition — never
   * as the authority on whether a bid landed (the server arbitrates that).
   */
  onComplete?: () => void;
  className?: string;
}

/** Compact default: "2d 3h 4m 5s" (omitting leading zero units), "1m 30s", etc. */
function defaultRender(r: TimeRemaining): string {
  const parts: string[] = [];
  if (r.days) parts.push(`${r.days}d`);
  if (r.days || r.hours) parts.push(`${r.hours}h`);
  if (r.days || r.hours || r.minutes) parts.push(`${r.minutes}m`);
  parts.push(`${r.seconds}s`);
  return parts.join(' ');
}

export function LiveCountdown({
  end,
  start,
  nowMs,
  children,
  endedLabel = 'Awaiting result',
  onComplete,
  className,
}: LiveCountdownProps): ReactElement {
  // Keep the latest nowMs / onComplete in refs so a fresh closure each render
  // doesn't reset the 1s tick (the effect keys only on start/end).
  const nowRef = useRef(nowMs);
  nowRef.current = nowMs;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [remaining, setRemaining] = useState<TimeRemaining>(() =>
    timeRemaining({ start, end, now: (nowRef.current ?? Date.now)() }),
  );

  // Fires onComplete once per window. Re-armed below when start/end change, so
  // an anti-snipe extension's fresh window can complete later.
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false; // re-anchor on a new window (AC 3)
    const tick = () => {
      const next = timeRemaining({ start, end, now: (nowRef.current ?? Date.now)() });
      setRemaining(next);
      if (!completedRef.current && next.status === 'ended') {
        completedRef.current = true;
        onCompleteRef.current?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [start, end]);

  return (
    // suppressHydrationWarning: the ticking text legitimately differs between the
    // SSR render ("moment of request") and hydration ("right now") — the
    // standard React posture for live time content. Guards SSR'd surfaces
    // (card grids) from a hydration-mismatch warning without a null-flash.
    <span className={className} suppressHydrationWarning>
      {remaining.status === 'ended'
        ? endedLabel
        : children
          ? children(remaining)
          : defaultRender(remaining)}
    </span>
  );
}
